import { ReactElement } from 'react'
import { Alert, Stack, Typography } from '@mui/material'
import { SwitchLabeled } from '@ui/actions'
import { CoordinatePointInput, Select } from '@ui/forms'
import { useControlStore } from '@editorStores'
import useWallUpdate from '../../hooks/useWallUpdate'
import { INVALID_WALL_UPDATE_MESSAGE } from '../../utils'
import WallLengthAdjustmentFields from '../WallLengthAdjustmentFields'

interface Props {
  /** the saved wall, or the drawn draft */
  wall: ShapeObject
  isDraft: boolean
  isPlacementEditable: boolean
  /** offer "Intelligentes Verschieben" (only moves in the scene use it) */
  showIntelligentWallEdit: boolean
  /** Disable all inputs, e.g. while the wall geometry is being saved. */
  disabled?: boolean
}

// Coordinates are stored as typed, so a wall can also be moved across its axis.
const FormFields = ({
  wall,
  isDraft,
  isPlacementEditable,
  showIntelligentWallEdit,
  disabled = false,
}: Props): ReactElement => {
  const intelligentWallEditEnabled = useControlStore(state => state.intelligentWallEditEnabled)
  const setIntelligentWallEditEnabled = useControlStore(
    state => state.setIntelligentWallEditEnabled,
  )

  const update = useWallUpdate(wall, isDraft)

  return (
    <Stack spacing={1}>
      <Stack direction="column" spacing={1}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography fontSize="inherit" color="red">
            Start
          </Typography>
          <CoordinatePointInput
            label="Start"
            name="start"
            pointManipulation={{ x: { disabled }, y: { disabled }, z: { disabled: true } }}
            disableReplace={disabled}
          />
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center">
          <Typography fontSize="inherit">Ende</Typography>
          <CoordinatePointInput
            label="Ende"
            name="end"
            pointManipulation={{ x: { disabled }, y: { disabled }, z: { disabled: true } }}
            disableReplace={disabled}
          />
        </Stack>
      </Stack>

      {update.kind === 'invalid' && (
        <Alert severity="warning" data-cy="alert-invalid-wall-update">
          {INVALID_WALL_UPDATE_MESSAGE}
        </Alert>
      )}

      <Select
        label="Platzierung auswählen"
        name="placement"
        options={[
          {
            value: 'External',
            label: 'Extern',
          },
          {
            value: 'Internal',
            label: 'Intern',
          },
        ]}
        disabled={disabled || !isPlacementEditable}
        data-cy="select-placement"
      />

      <WallLengthAdjustmentFields />

      {showIntelligentWallEdit && (
        <SwitchLabeled
          checked={intelligentWallEditEnabled}
          onChange={event => setIntelligentWallEditEnabled(event.target.checked)}
          label="Intelligentes Verschieben"
          data-cy="switch-intelligent-wall-edit"
          infoTooltip={{
            title:
              'Beim orthogonalen Verschieben werden angeschlossene, kartesische Wände (Start/Ende genau auf dieser Wand) mit verkürzt oder verlängert.',
          }}
        />
      )}
    </Stack>
  )
}

export default FormFields
